/* eslint-disable eqeqeq */
import { useRef, useState } from 'react';
import type { ChangeEvent, FocusEvent } from 'react';
import { formatLegacyNumber } from '../formUtils';

/**
 * ★ THE ACCESSORY SELECTOR's NUMBER INPUT — legacy's `validateNumberField`
 * DISPLAY pass, which React never ran.
 *
 * Legacy `render()` ends with (accessory.selector.js:406-410):
 *
 *   const numberInputs = this.$el.find(
 *     'input[data-type="number"], input[data-formatter_type="decimal"]');
 *   numberInputs.each(… self.validateNumberField(input) …);
 *
 * i.e. EVERY number input in the dialog is reformatted on EVERY render —
 * `data-formatter_type="integer"` → `Math.round` + thousands separators,
 * `decimal` (or `data-step="0.01"`) → `toFixed(2)` + thousands separators
 * (`validateNumberField` :2952-2988 → `fn.uni_tools.formatNumberWithCommaSeparator`
 * :973). React printed the raw model value instead, so the two stacks showed
 * measurably different text in the same box:
 *
 *   legacy `1,200`   → React `1200`                 (Desired Face Velocity)
 *   legacy `20,000`  → React `20000`                (Airflow)
 *   legacy `12.00`   → React `12`
 *   legacy `51.94`   → React `51.93600000000001`    (a distance-to-wall readout)
 *   legacy `0.24`    → React `0.2392`               (Air Pressure Drop)
 *
 * This predates the combo-louver port — it affects ALL FOUR accessory types
 * (damper / louver / hood / opening) — but it was HIDDEN until wave 2
 * un-occluded the right-hand column, so QA will report it as new. Hence it lands
 * here. `formatLegacyNumber(value, step)` in formUtils is already the faithful
 * port of that formatter (including the "no comma → keep the original string, so
 * toFixed decimals survive" quirk), so this is a display wiring job, not new math.
 *
 * ★ HOW TYPING SURVIVES THE FORMATTER. A naive `value={format(model)}` makes
 * multi-character entry impossible: the first keystroke commits, the re-render
 * rewrites the box ("7" → "7.00") and moves the caret, and the next character
 * lands in the wrong place — exactly the trap wave 2 hit on the enclosure trio.
 * So the formatted text is shown ONLY while the field is not being edited: on
 * focus the current (formatted) text becomes a local draft, the draft wins for as
 * long as the field has focus, and blur drops it so the freshly formatted model
 * value comes back. Legacy behaves the same way — its reformat happens on
 * `render()`, and the user is not typing into a box that is being re-rendered.
 *
 * ★ COMMIT SEMANTICS ARE PRESERVED, NOT CHANGED — `commitOn`:
 *  · `'change'` (default) — fire on every input event, which is what the shared
 *    accessory rows have always done.
 *  · `'blur'`  — fire once, on blur/Enter, and only when the value actually moved.
 *    This is what wave 2 established for the combo enclosure W/H/D because legacy
 *    binds only `change .model_field` (its `keyup .model_field` entry is commented
 *    out, :56-57), and because the input-time CMS clamp + the DOM-reading
 *    `_comboReady`/`_fetchComboAwDims` require whole values. That behaviour is
 *    carried over verbatim here.
 */
export interface SelectorNumberInputProps {
  name: string;
  /** the RAW model value — formatting happens here. */
  value: unknown;
  formatter?: 'decimal' | 'integer';
  className?: string;
  readOnly?: boolean;
  disabled?: boolean;
  min?: unknown;
  max?: unknown;
  autoComplete?: string;
  /**
   * emit `data-type="number"`? The model fields do (`louver.selector.tmpl:217`
   * etc.); the `.position_field` distances deliberately do NOT (:328 — they carry
   * only `data-formatter_type="decimal"`), which is exactly why legacy's
   * `changeModelField` number coercion (:2314-2316) never applies to them while
   * the render-time display pass does.
   */
  dataTypeNumber?: boolean;
  /** see the `commitOn` note above. */
  commitOn?: 'change' | 'blur';
  /**
   * ★ OPT-IN: strip the thousands separators while the box has focus, and put
   * them back on blur — `10,000` at rest, `10000` under the caret.
   *
   * This is `NumericInputFormatter`'s focus/blur pair
   * (`universal_fn/formatter.js:207-225`), the OTHER legacy formatter: its
   * `onfocus` writes back the DECODED value at the mode's decimal count
   * (`value.toFixed(0)` for integer, `toFixed(2)` for decimal — so only
   * integer fields visibly change), and its `onblur` re-encodes through
   * `setValue`. `encode` itself short-circuits on
   * `element === document.activeElement` (:128-129) so nothing re-commas the
   * box mid-edit.
   *
   * It is opt-in because the two legacy formatters do NOT overlap: this one is
   * installed by `bindFormatter` sweeps over `[data-formatter_type]`
   * (`component_view.js:855-864` and ~30 other call sites) and never touches
   * the selector dialogs, which format through `validateNumberField` at render
   * time only. Turning it on by default would change every accessory-selector
   * number box away from the behaviour they were verified against.
   */
  unformatOnFocus?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

/**
 * `NumericInputFormatter.onfocus` (:207-218) — the decoded value rendered at
 * the mode's decimal count, i.e. no separators.
 */
function focusText(value: unknown, formatter: 'decimal' | 'integer'): string {
  if (value == null || value === '') return '';
  const n = Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(n)) return String(value);
  return formatter === 'integer' ? String(Math.round(n)) : n.toFixed(2);
}

export const SelectorNumberInput = ({
  name,
  value,
  formatter = 'decimal',
  className,
  readOnly,
  disabled,
  min,
  max,
  autoComplete,
  dataTypeNumber = true,
  commitOn = 'change',
  unformatOnFocus = false,
  onChange,
}: SelectorNumberInputProps) => {
  /** non-null only while this field has focus (see the typing note above). */
  const [draft, setDraft] = useState<string | null>(null);
  /** the value at focus time — a native `change` fires only if it moved. */
  const atFocus = useRef<string>('');
  const step = formatter === 'decimal' ? 0.01 : undefined;
  const shown = draft ?? formatLegacyNumber(value, step);
  const locked = !!readOnly || !!disabled;
  return (
    <input
      name={name}
      type="text"
      data-type={dataTypeNumber ? 'number' : undefined}
      data-formatter_type={formatter}
      data-step={formatter === 'decimal' ? '0.01' : undefined}
      data-min={min as string | number | undefined}
      data-max={max as string | number | undefined}
      className={className}
      value={shown}
      readOnly={readOnly}
      disabled={disabled}
      tabIndex={locked ? -1 : undefined}
      autoComplete={autoComplete}
      onFocus={(e) => {
        const opening = unformatOnFocus ? focusText(value, formatter) : e.target.value;
        atFocus.current = opening;
        setDraft(opening);
      }}
      onChange={(e) => {
        setDraft(e.target.value);
        if (!locked && commitOn === 'change') onChange?.(e);
      }}
      onBlur={(e: FocusEvent<HTMLInputElement>) => {
        // fire BEFORE dropping the draft so the handler (and the DOM reads inside
        // it — `_fetchComboAwDims`/`_comboReady` read these inputs directly) still
        // see the typed text, exactly as a native `change` does.
        if (!locked && commitOn === 'blur' && e.target.value !== atFocus.current) {
          onChange?.(e as unknown as ChangeEvent<HTMLInputElement>);
        }
        setDraft(null);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  );
};

/**
 * `validateNumberField`'s COMMIT half (:2952-2988), reached from
 * `changeModelField` :2314-2316:
 *
 *   if (this.$el.find(`input[data-type="number"][name="${name}"]`).length)
 *     value = self.validateNumberField(…)
 *
 * so a model number field commits the PARSED, ROUNDED number — never the raw box
 * text. That was harmless while React displayed raw digits; it becomes load-bearing
 * the moment the display carries thousands separators, because editing a box
 * showing `20,000` would otherwise commit the string `"20,0001"`.
 *
 * One deliberate hardening: legacy strips only the FIRST comma
 * (`.replace(",", "")`), so a value carrying two of them becomes `NaN || 0` →
 * **0**. Unreachable in this dialog (every field's max is < 1 000 000, i.e. at most
 * one separator) and a silent zeroing would be actively harmful, so the strip is
 * global here.
 *
 * ⚠ THE `data-min`/`data-max` CLAMP HALF IS DELIBERATELY NOT PORTED HERE, for two
 * reasons that both come back to the same thing:
 *  · legacy's clamp is inseparable from a DOM WRITE-BACK (`input.val(value)`
 *    inside `validateNumberField`) — and legacy can afford it because
 *    `change .model_field` fires on BLUR, its keyup bindings being commented out
 *    (:56-57). React's shared accessory rows commit on every input event, so a
 *    write-back would reformat and re-caret the box mid-typing — the exact defect
 *    wave 2 had to fix on the enclosure trio.
 *  · the only fields whose clamp is actually load-bearing are the combo enclosure
 *    W/H/D, because `_fetchComboAwDims`/`_comboReady` read them straight off the
 *    DOM — and those already carry wave 2's dedicated input-time CMS clamp,
 *    write-back included (`AccessorySelector.onModelField`, legacy :2317-2337).
 * So React continues not to clamp the other number fields at commit time. That is
 * a PRE-EXISTING divergence (React never clamped them), not one introduced here.
 *
 * Returns `null` for an empty box — legacy yields `undefined` and then
 * `staging_model.set({[name]: value !== "" ? value : null})` stores that; React's
 * `onModelField` has always normalised empty → `null`, and that pre-existing
 * choice is left untouched.
 */
export function commitNumberValue(el: HTMLInputElement): number | null {
  const raw = el.value;
  if (!raw.length) return null;
  const formatter =
    el.dataset.formatter_type ?? (el.dataset.step == '0.01' ? 'decimal' : 'integer');
  const v = Number(raw.replace(/,/g, '')) || 0;
  return formatter === 'integer' ? Math.round(v) : Math.round(v * 100) / 100;
}

export default SelectorNumberInput;
